// src/hooks/hooks-admin/useDashboardData.js
import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

// 📦 MAPPA STATI ORDINI (database → UI)
export const orderStatusMap = {
    'in_attesa': { label: 'In attesa', color: '#f39c12', bgColor: '#fff3e0', icon: '⏳' },
    'in_elaborazione': { label: 'In elaborazione', color: '#3498db', bgColor: '#e3f2fd', icon: '⚙️' },
    'spedito': { label: 'Spedito', color: '#9b59b6', bgColor: '#f3e5f5', icon: '🚚' },
    'consegnato': { label: 'Consegnato', color: '#27ae60', bgColor: '#e8f5e9', icon: '✅' },
    'annullato': { label: 'Annullato', color: '#e74c3c', bgColor: '#fdecea', icon: '❌' }
};

// Valori predefiniti per stati sconosciuti
const defaultStatus = { label: 'Sconosciuto', color: '#95a5a6', bgColor: '#f5f5f5', icon: '❓' };

// Funzioni helper per gli stati
export const getOrderStatus = (status) => orderStatusMap[status] || defaultStatus;
export const getStatusLabel = (status) => getOrderStatus(status).label;
export const getStatusColor = (status) => getOrderStatus(status).color;
export const getStatusBgColor = (status) => getOrderStatus(status).bgColor;
export const getStatusIcon = (status) => getOrderStatus(status).icon;

const useDashboardData = () => {
    // State
    const [orders, setOrders] = useState([]);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [orderStats, setOrderStats] = useState({
        by_status: [],
        recent_days: [],
        total_orders: 0,
        total_revenue: 0
    });
    const [userSummary, setUserSummary] = useState({
        total: 0,
        active: 0,
        newThisMonth: 0
    });
    const [loading, setLoading] = useState({
        orders: false,
        stats: false,
        orderDetail: false,
        updateStatus: false
    });
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({
        status: '',
        startDate: '',
        endDate: '',
        search: ''
    });
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0
    });
    const [updatingOrder, setUpdatingOrder] = useState(null);

    // Configurazione base axios
    const baseConfig = useMemo(() => ({
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' }
    }), []);

    // 📊 Formatta valuta in Euro
    const formatCurrency = useCallback((amount) => {
        return new Intl.NumberFormat('it-IT', { 
            style: 'currency', 
            currency: 'EUR',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount || 0);
    }, []);

    // 📅 Formatta data
    const formatDate = useCallback((dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return new Intl.DateTimeFormat('it-IT', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        }).format(date);
    }, []);

    // 📅 Formatta data solo giorno
    const formatShortDate = useCallback((dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return new Intl.DateTimeFormat('it-IT', {
            day: '2-digit',
            month: 'short'
        }).format(date);
    }, []);

    // 📊 Statistiche ordini con etichette leggibili
    const getFormattedStats = useCallback(() => {
        const statusStats = orderStats.by_status.map(item => {
            const statusInfo = getOrderStatus(item.stato);
            return {
                ...item,
                statusKey: item.stato,
                statusLabel: statusInfo.label,
                statusColor: statusInfo.color,
                statusBgColor: statusInfo.bgColor,
                statusIcon: statusInfo.icon,
                count: parseInt(item.count),
                total_amount: parseFloat(item.total_amount || 0)
            };
        });

        const recentStats = (orderStats.recent_days || []).map(item => ({
            date: item.date,
            formattedDate: formatShortDate(item.date),
            orders: parseInt(item.count),
            revenue: parseFloat(item.total || 0),
            formattedRevenue: formatCurrency(item.total || 0)
        }));

        return {
            by_status: statusStats,
            recent_days: recentStats,
            total_orders: orderStats.total_orders,
            total_revenue: orderStats.total_revenue,
            formatted_total_revenue: formatCurrency(orderStats.total_revenue)
        };
    }, [orderStats, formatShortDate, formatCurrency]);

    // 📦 Formatta lista ordini con stati leggibili
    const getFormattedOrders = useCallback(() => {
        return orders.map(order => {
            const statusInfo = getOrderStatus(order.stato);
            return {
                ...order,
                statusLabel: statusInfo.label,
                statusColor: statusInfo.color,
                statusBgColor: statusInfo.bgColor,
                statusIcon: statusInfo.icon,
                formattedTotal: formatCurrency(order.totale),
                formattedDate: formatDate(order.data_creazione),
                shortDate: formatShortDate(order.data_creazione),
                clienteName: `${order.cliente?.nome || ''} ${order.cliente?.cognome || ''}`.trim() || order.cliente?.email || 'Cliente'
            };
        });
    }, [orders, formatCurrency, formatDate, formatShortDate]);

    // 🔍 Ottieni opzioni per il filtro stati
    const getStatusFilterOptions = useCallback(() => {
        return Object.entries(orderStatusMap).map(([value, { label, color }]) => ({
            value,
            label,
            color
        }));
    }, []);

    // 📈 Dati per grafici
    const getChartData = useCallback(() => {
        const formattedStats = getFormattedStats();
        
        return {
            pieChart: formattedStats.by_status.map(stat => ({
                name: stat.statusLabel,
                value: stat.count,
                color: stat.statusColor,
                icon: stat.statusIcon
            })),
            barChart: formattedStats.recent_days.map(day => ({
                date: day.formattedDate,
                orders: day.orders,
                revenue: day.revenue,
                formattedRevenue: day.formattedRevenue
            })),
            totals: {
                totalOrders: formattedStats.total_orders,
                totalRevenue: formattedStats.total_revenue,
                formattedTotalRevenue: formattedStats.formatted_total_revenue
            }
        };
    }, [getFormattedStats]);

    // 🎴 Card statistiche per la dashboard
    const getStatsCards = useCallback(() => {
        const formattedStats = getFormattedStats();
        
        const pendingOrders = formattedStats.by_status.find(s => s.statusKey === 'in_attesa')?.count || 0;
        
        const recentDays = formattedStats.recent_days;
        let orderTrend = null;
        let revenueTrend = null;
        
        if (recentDays.length >= 2) {
            const todayOrders = recentDays[0]?.orders || 0;
            const yesterdayOrders = recentDays[1]?.orders || 0;
            orderTrend = yesterdayOrders > 0 
                ? Math.round(((todayOrders - yesterdayOrders) / yesterdayOrders) * 100)
                : todayOrders > 0 ? 100 : 0;
            
            const todayRevenue = recentDays[0]?.revenue || 0;
            const yesterdayRevenue = recentDays[1]?.revenue || 0;
            revenueTrend = yesterdayRevenue > 0
                ? Math.round(((todayRevenue - yesterdayRevenue) / yesterdayRevenue) * 100)
                : todayRevenue > 0 ? 100 : 0;
        }
        
        return {
            totalOrders: {
                value: formattedStats.total_orders,
                label: 'Totale Ordini',
                icon: '📦',
                color: '#8B0000',
                trend: orderTrend,
                trendLabel: orderTrend !== null ? `${orderTrend >= 0 ? '+' : ''}${orderTrend}% rispetto a ieri` : null
            },
            totalRevenue: {
                value: formattedStats.total_revenue,
                formatted: formattedStats.formatted_total_revenue,
                label: 'Fatturato Totale',
                icon: '💰',
                color: '#27ae60',
                trend: revenueTrend,
                trendLabel: revenueTrend !== null ? `${revenueTrend >= 0 ? '+' : ''}${revenueTrend}% rispetto a ieri` : null
            },
            pendingOrders: {
                value: pendingOrders,
                label: 'Ordini in Attesa',
                icon: '⏳',
                color: '#f39c12',
                trend: null,
                statusInfo: getOrderStatus('in_attesa')
            },
            totalUsers: {
                value: userSummary.total || 0,
                label: 'Utenti Registrati',
                icon: '👥',
                color: '#3498db',
                trend: null,
                subText: `${userSummary.newThisMonth || 0} nuovi questo mese`
            }
        };
    }, [getFormattedStats, userSummary]);

    // 🎨 Helper per ottenere stile badge dello stato
    const getStatusBadgeStyle = useCallback((status) => {
        const { color, bgColor } = getOrderStatus(status);
        return {
            backgroundColor: bgColor,
            color: color,
            padding: '4px 12px',
            borderRadius: '20px',
            fontSize: '12px',
            fontWeight: '500',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px'
        };
    }, []);

    const fetchDashboardStats = useCallback(async () => {
        setLoading(prev => ({ ...prev, stats: true }));
        setError(null);
        
        try {
            // 1. Statistiche ordini
            const ordersResponse = await axios.get('/api/admin/orders/stats', baseConfig);
            if (ordersResponse.data.success) {
                setOrderStats(ordersResponse.data.stats);
            }
            
            // 2. Conteggio utenti
            try {
                const usersRes = await axios.get('/api/admin/users', {
                    ...baseConfig,
                    params: { limit: 1, page: 1 }
                });
                
                if (usersRes.data.success) {
                    // La risposta ha 'total' direttamente, non 'pagination.total'
                    const totalUsers = usersRes.data.total || 0;
                    
                    setUserSummary(prev => ({
                        ...prev,
                        total: totalUsers
                    }));
                    
                    console.log('✅ Utenti totali dal database:', totalUsers);
                }
                
            } catch (userErr) {
                console.warn('Errore recupero utenti:', userErr.message);
            }
            
        } catch (err) {
            console.error('❌ Errore fetch stats:', err.message);
            setError(err.response?.data?.message || err.message);
        } finally {
            setLoading(prev => ({ ...prev, stats: false }));
        }
    }, [baseConfig]);

    // 🌐 Fetch lista ordini
    const fetchOrders = useCallback(async (page = pagination.page) => {
        setLoading(prev => ({ ...prev, orders: true }));
        
        try {
            const params = {
                page,
                limit: pagination.limit,
                ...(filters.status && { status: filters.status }),
                ...(filters.startDate && { startDate: filters.startDate }),
                ...(filters.endDate && { endDate: filters.endDate }),
                ...(filters.search && { search: filters.search })
            };
            
            const response = await axios.get('/api/admin/orders', { ...baseConfig, params });
            
            if (response.data.success) {
                setOrders(response.data.orders || []);
                setPagination(prev => ({
                    ...prev,
                    page: response.data.pagination?.page || page,
                    total: response.data.pagination?.total || 0,
                    totalPages: response.data.pagination?.totalPages || 0
                }));
            }
        } catch (err) {
            console.error('❌ Errore fetch orders:', err.message);
        } finally {
            setLoading(prev => ({ ...prev, orders: false }));
        }
    }, [filters, pagination.limit, pagination.page, baseConfig]);

    // 🌐 Fetch dettaglio ordine
    const fetchOrderDetail = useCallback(async (orderId) => {
        setLoading(prev => ({ ...prev, orderDetail: true }));
        
        try {
            const response = await axios.get(`/api/admin/orders/${orderId}`, baseConfig);
            if (response.data.success) {
                const order = response.data.order;
                const statusInfo = getOrderStatus(order.stato);
                const enrichedOrder = {
                    ...order,
                    statusLabel: statusInfo.label,
                    statusColor: statusInfo.color,
                    statusBgColor: statusInfo.bgColor,
                    statusIcon: statusInfo.icon,
                    formattedTotal: formatCurrency(order.totale),
                    formattedSubtotal: formatCurrency(order.subtotale),
                    formattedShipping: formatCurrency(order.costo_spedizione),
                    formattedDate: formatDate(order.data_creazione),
                    items: order.items?.map(item => ({
                        ...item,
                        formattedPrice: formatCurrency(item.prezzo_unitario),
                        formattedTotal: formatCurrency(item.prezzo_totale)
                    }))
                };
                setSelectedOrder(enrichedOrder);
                return enrichedOrder;
            }
        } catch (err) {
            console.error('❌ Errore fetch detail:', err.message);
        } finally {
            setLoading(prev => ({ ...prev, orderDetail: false }));
        }
        return null;
    }, [baseConfig, formatCurrency, formatDate]);

    // 🔄 Update stato ordine
    const updateOrderStatus = useCallback(async (orderId, newStatus) => {
        setUpdatingOrder(orderId);
        setLoading(prev => ({ ...prev, updateStatus: true }));
        
        try {
            const response = await axios.put(`/api/admin/orders/${orderId}/status`, 
                { status: newStatus },
                baseConfig
            );
            
            if (response.data.success) {
                setOrders(prev => prev.map(order => 
                    order.id === orderId 
                        ? { ...order, stato: newStatus }
                        : order
                ));
                
                if (selectedOrder?.id === orderId) {
                    const statusInfo = getOrderStatus(newStatus);
                    setSelectedOrder(prev => ({ 
                        ...prev, 
                        stato: newStatus,
                        statusLabel: statusInfo.label,
                        statusColor: statusInfo.color,
                        statusBgColor: statusInfo.bgColor,
                        statusIcon: statusInfo.icon
                    }));
                }
                
                fetchDashboardStats();
                return true;
            }
        } catch (err) {
            console.error('❌ Errore update status:', err.message);
            return false;
        } finally {
            setUpdatingOrder(null);
            setLoading(prev => ({ ...prev, updateStatus: false }));
        }
    }, [selectedOrder, fetchDashboardStats, baseConfig]);

    // 📄 Cambia pagina
    const changePage = useCallback((newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            setPagination(prev => ({ ...prev, page: newPage }));
        }
    }, [pagination.totalPages]);

    // 🔍 Applica filtri
    const applyFilters = useCallback((newFilters) => {
        setFilters(prev => ({ ...prev, ...newFilters }));
        setPagination(prev => ({ ...prev, page: 1 }));
    }, []);

    // 🔄 Reset filtri
    const resetFilters = useCallback(() => {
        setFilters({ status: '', startDate: '', endDate: '', search: '' });
        setPagination(prev => ({ ...prev, page: 1 }));
    }, []);

    // Effetti
    useEffect(() => {
        fetchDashboardStats();
    }, [fetchDashboardStats]);
    
    useEffect(() => {
        fetchOrders(pagination.page);
    }, [fetchOrders, pagination.page]);

    // Valori memorizzati per evitare ricalcoli
    const formattedOrders = useMemo(() => getFormattedOrders(), [getFormattedOrders]);
    const statsCards = useMemo(() => getStatsCards(), [getStatsCards]);
    const chartData = useMemo(() => getChartData(), [getChartData]);
    const formattedStats = useMemo(() => getFormattedStats(), [getFormattedStats]);
    const statusFilterOptions = useMemo(() => getStatusFilterOptions(), [getStatusFilterOptions]);

    return {
        // State
        orders: formattedOrders,
        rawOrders: orders,
        selectedOrder,
        loading,
        error,
        filters,
        pagination,
        updatingOrder,
        
        // Dati formattati
        statsCards,
        chartData,
        formattedStats,
        statusFilterOptions,
        
        // Helper stati
        getOrderStatus,
        getStatusLabel,
        getStatusColor,
        getStatusBgColor,
        getStatusIcon,
        getStatusBadgeStyle,
        
        // Azioni
        fetchDashboardStats,
        fetchOrders,
        fetchOrderDetail,
        updateOrderStatus,
        applyFilters,
        resetFilters,
        changePage,
        
        // Helpers generali
        isUpdatingOrder: useCallback((orderId) => updatingOrder === orderId, [updatingOrder]),
        formatCurrency,
        formatDate,
        formatShortDate,
        
        // Loading states specifici
        isLoading: loading.stats || loading.orders,
        isLoadingStats: loading.stats,
        isLoadingOrders: loading.orders,
        isUpdatingStatus: loading.updateStatus
    };
};

export default useDashboardData;
